/**
 * Collection list carousel
 * Transform-driven infinite carousel.
 *
 * The grid is translated horizontally via CSS transform; native overflow scroll
 * is disabled so we have full deterministic control over position. A clone of
 * every original item is appended so movement past the last original lands on
 * an identical-looking strip, after which we silently reset the position back
 * to the equivalent point inside the originals. Position is tracked in pixels
 * regardless of how fast the user clicks: each click only mutates a single
 * number, the CSS transition retargets continuously, and the post-animation
 * normalisation runs once after the user stops clicking.
 */
const TRANSITION_MS = 450;

class CollectionListCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-collection-list-track]');
    this.grid = this.track?.querySelector('.collection-list__grid');
    this.prevBtn = this.querySelector('[data-collection-list-prev]');
    this.nextBtn = this.querySelector('[data-collection-list-next]');
    this.counter = this.querySelector('[data-collection-list-counter]');
    if (!this.track || !this.grid) return;

    this.originalItems = Array.from(this.grid.children);
    this.originalCount = this.originalItems.length;
    if (this.originalCount === 0) return;

    this.itemWidth = 0;
    this.gap = 0;
    this.step = 0;
    this.originalWidth = 0;
    this.position = 0;

    this.cloneItems();

    this.prevBtn?.addEventListener('click', () => this.goPrev());
    this.nextBtn?.addEventListener('click', () => this.goNext());
    this.grid.addEventListener('transitionend', (e) => this.onTransitionEnd(e));
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.track);

    this.computeMetrics();
    this.applyTransform(false);
    this.updateCounter();

    // Trascinamento con dito e mouse: il carosello si muove con transform e
    // overflow hidden, quindi senza questo il gesto non produrrebbe nulla.
    window.lionTrascinamentoCarosello?.(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  cloneItems() {
    this.originalItems.forEach((el) => {
      const clone = el.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.clone = 'true';
      this.grid.appendChild(clone);
    });
  }

  computeMetrics() {
    if (this.originalItems.length === 0) return;
    // Una scheda puo' non essere disegnata affatto: succede quando il prodotto
    // e' escluso dalle collezioni, dove il <li> resta vuoto e il CSS lo
    // nasconde. Misurando quello la larghezza risultava zero e il passo
    // diventava il solo spazio fra le schede, otto pixel: il carosello si
    // muoveva di un'inezia a ogni gesto. Si misura la prima scheda che
    // esiste davvero.
    const riferimento = Array.from(this.originalItems).find(
      (elemento) => elemento.getBoundingClientRect().width > 0
    );
    if (!riferimento) return;
    this.itemWidth = riferimento.getBoundingClientRect().width;
    const style = window.getComputedStyle(this.grid);
    this.gap = parseFloat(style.columnGap || style.gap) || 0;
    this.step = this.itemWidth + this.gap;
    const firstClone = this.grid.children[this.originalCount];
    this.originalWidth = firstClone ? firstClone.offsetLeft : this.originalCount * this.step;
  }

  handleResize() {
    this.computeMetrics();
    if (!this.step) return;
    // Re-anchor current position to nearest item to avoid drift after layout changes
    const index = Math.round(this.position / this.step);
    this.position = index * this.step;
    this.applyTransform(false);
  }

  applyTransform(animate) {
    this.grid.style.transition = animate ? `transform ${TRANSITION_MS}ms ease` : 'none';
    this.grid.style.transform = `translate3d(${-this.position}px, 0, 0)`;
  }

  goNext() {
    // If we've already crossed into the clone strip on a previous click, snap
    // back instantly before moving forward so position never grows unbounded.
    if (this.position >= this.originalWidth) {
      this.position -= this.originalWidth;
      this.applyTransform(false);
      this.forceReflow();
    }
    this.position += this.step;
    this.applyTransform(true);
    this.updateCounter();
  }

  goPrev() {
    if (this.position <= 0) {
      // Jump forward into the clone area without animation, then animate back
      this.position += this.originalWidth;
      this.applyTransform(false);
      this.forceReflow();
    }
    this.position -= this.step;
    this.applyTransform(true);
    this.updateCounter();
  }

  forceReflow() {
    // Force the browser to commit the non-animated transform before applying
    // the next animated one, preventing the two changes from being merged.
    void this.grid.offsetWidth;
  }

  onTransitionEnd(event) {
    if (event.propertyName !== 'transform') return;
    if (this.position >= this.originalWidth) {
      this.position -= this.originalWidth;
      this.applyTransform(false);
    } else if (this.position < 0) {
      this.position += this.originalWidth;
      this.applyTransform(false);
    }
  }

  updateCounter() {
    if (!this.counter) return;
    const count = this.originalCount;
    const index = Math.round(this.position / this.step);
    const display = ((index % count) + count) % count + 1;
    this.counter.textContent = `${display} / ${count}`;
  }
}

customElements.define('lion-collection-list-carousel', CollectionListCarousel);
